import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col select-none" }
const _hoisted_2 = { class: "font-semibold text-2xl mb-10" }
const _hoisted_3 = { class: "w-full mb-4" }
const _hoisted_4 = { class: "text-left mb-2 text-brown text-base leading-[20px] sf-pro" }
const _hoisted_5 = { class: "flex gap-4 mb-10" }
const _hoisted_6 = { class: "flex justify-between items-center px-4 text-base" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "w-full" }
const _hoisted_9 = { class: "text-brown text-base leading-[20px] select-none mb-2 sf-pro" }
const _hoisted_10 = { class: "text-white font-semibold text-xl leading-[24px]" }

import BaseModal from "@/views/Auth/components/BaseModal.vue";
import { useI18n } from "vue-i18n";
import TextInput from "@/components/inputs/TextInput.vue";
import { computed, ref, watch } from "vue";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import AppDropdown from "@/components/inputs/AppDropdown.vue";
import { FamilyMember, Gender, GENDERS } from "@/types/familyTypes";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import { useFamilyStore } from "@/stores/familyStore";
import { useToastNotificationStore } from "@/stores/toastNotificationStore";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";


export default /*@__PURE__*/_defineComponent({
  __name: 'FamilyMemberModal',
  props: {
    show: { type: Boolean },
    editableFamilyMember: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

watch(
  () => props.editableFamilyMember,
  (newValue: FamilyMember | undefined) => {
    if (newValue === undefined) {
      name.value = "";
      gender.value = "male";
      birthday.value = null;
      location.value = undefined;
      timezone.value = "";
      cityName.value = "";
      return;
    }

    name.value = newValue.name;
    gender.value = newValue.gender;
    birthday.value = new Date(newValue.birthdate);
    location.value = { lat: newValue.latitude, lng: newValue.longitude };
    timezone.value = newValue.timezone;
    cityName.value = newValue.city;
  }
);

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");
const { addFamilyMember, updateFamilyMember } = useFamilyStore();
const toastStore = useToastNotificationStore();

const name = ref<string>("");
const gender = ref<Gender>("male");
const birthday = ref<Date | null>(null);
const location = ref<{ lat: number; lng: number }>();
const timezone = ref<string>("");
const cityName = ref<string>("");

const isGenderDropdownOpen = ref(false);
const waitApiCall = ref(false);
const isFormValid = computed(() => {
  return (
    name.value.length > 0 &&
    location.value !== undefined &&
    birthday.value !== null &&
    cityName.value.length > 0 &&
    timezone.value.length > 0
  );
});

function handleSelectGender(newGender: Gender) {
  gender.value = newGender;
  isGenderDropdownOpen.value = false;
}

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  location.value = {
    lat: payload.location.lat(),
    lng: payload.location.lng(),
  };
  cityName.value = payload.locationName;
  timezone.value = normalizeGoogleTimeZone(payload.timeZone);
}

async function handleSubmit() {
  if (waitApiCall.value || !isFormValid.value) {
    return;
  }
  try {
    waitApiCall.value = true;
    const request = {
      name: name.value,
      city: cityName.value,
      latitude: location.value?.lat as number,
      longitude: location.value?.lng as number,
      timezone: timezone.value,
      gender: gender.value,
      birthdate: birthday.value as Date,
    };
    if (props.editableFamilyMember !== undefined) {
      await updateFamilyMember(request, props.editableFamilyMember);
    } else {
      await addFamilyMember(request);
    }
    emit("close");
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    show: __props.show,
    maxHeight: '504px',
    maxWidth: '480px',
    onClose: _cache[3] || (_cache[3] = ($event: any) => (emit('close')))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("p", _hoisted_2, _toDisplayString(__props.editableFamilyMember === undefined
            ? _unref(t)("family-table.add-title")
            : _unref(t)("family-table.update-title")), 1),
        _createVNode(TextInput, {
          label: _unref(t)('family-table.member.name'),
          modelValue: name.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
          type: "text",
          class: "mb-4"
        }, null, 8, ["label", "modelValue"]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)("family-table.member.city")), 1),
          _createVNode(GooglePlaceAutocompleteInput, {
            styleType: "outline",
            initialQuery: __props.editableFamilyMember?.city ?? undefined,
            onPlaceSelected: handlePlaceSelected
          }, null, 8, ["initialQuery"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(AppDropdown, {
            borderColor: "border-muted",
            labelWidthClass: "font-normal",
            label: _unref(t)('family-table.member.gender'),
            items: _unref(GENDERS),
            selectedItem: gender.value,
            isDropdownOpen: isGenderDropdownOpen.value,
            onToggle: _cache[1] || (_cache[1] = ($event: any) => (isGenderDropdownOpen.value = !isGenderDropdownOpen.value))
          }, {
            selectedItem: _withCtx(({ item }) => [
              _createElementVNode("span", _hoisted_6, [
                _createElementVNode("span", null, _toDisplayString(_unref(t)(`family-table.gender.${item}`)), 1),
                _createVNode(ChevronIcon, {
                  class: _normalizeClass(["transition-transform", isGenderDropdownOpen.value ? 'rotate-180' : 'rotate-0'])
                }, null, 8, ["class"])
              ])
            ]),
            dropdownItem: _withCtx(({ item }) => [
              _createElementVNode("div", {
                class: "flex items-center px-4 text-base h-10 hover:bg-divider cursor-pointer select-none",
                onClick: ($event: any) => (handleSelectGender(item))
              }, [
                _createElementVNode("p", null, _toDisplayString(_unref(t)(`family-table.gender.${item}`)), 1)
              ], 8, _hoisted_7)
            ]),
            _: 1
          }, 8, ["label", "items", "selectedItem", "isDropdownOpen"]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)("family-table.member.birthdate")), 1),
            _createVNode(DatePicker, {
              showDatePickIcon: false,
              initialDate: __props.editableFamilyMember?.birthdate ?? undefined,
              styleType: "outline",
              onDateSelected: _cache[2] || (_cache[2] = (newDate) => (birthday.value = newDate))
            }, null, 8, ["initialDate"])
          ])
        ]),
        _createElementVNode("button", {
          class: _normalizeClass(["bg-orange-gradient py-4 rounded-full", {
          'opacity-40 pointer-events-none': waitApiCall.value || !isFormValid.value,
        }]),
          onClick: handleSubmit
        }, [
          _createElementVNode("span", _hoisted_10, _toDisplayString(__props.editableFamilyMember === undefined
              ? _unref(t)("buttons.add")
              : _unref(t)("buttons.save")), 1)
        ], 2)
      ])
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})